import React, {useEffect, useRef, useState} from 'react'
import {useStopwatch} from 'react-timer-hook';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import {useCompleteSession, useStartSession} from "./hooks/useSession";
import {readPersistedTimerState, STOPWATCH_STATE_STORAGE_KEY} from "./timerStorage";

const sessionAudio = new Audio('/session-sounds/waterfall/waterfall-v2.mp3');
sessionAudio.loop = true

function MyTimer({project, task, authToken, onSessionStart, onSessionComplete}) {
    const [isCompleted, setCompleted] = useState(false)
    const [isStart, setStart] = useState(true)
    const [isMuted, setMuted] = useState(true)
    const [completionError, setCompletionError] = useState('')
    const [isCompleting, setIsCompleting] = useState(false)
    const [hasHydratedState, setHasHydratedState] = useState(false)
    const currentSessionIdRef = useRef(null)
    const hydrationInitializedRef = useRef(false)
    const {mutateAsync: startSession} = useStartSession(authToken)
    const {mutateAsync: completeSession} = useCompleteSession(authToken)

    const {
        seconds,
        minutes,
        hours,
        isRunning,
        start,
        pause,
        reset,
    } = useStopwatch({autoStart: false, interval: 20});
    const elapsedSeconds = (hours * 3600) + (minutes * 60) + seconds

    useEffect(() => {
        if (hydrationInitializedRef.current) {
            return
        }
        hydrationInitializedRef.current = true

        const persistedState = readPersistedTimerState(STOPWATCH_STATE_STORAGE_KEY)
        if (!persistedState) {
            setHasHydratedState(true)
            return
        }

        setMuted(Boolean(persistedState.isMuted))
        const persistedSessionId = persistedState.currentSessionId ?? null
        currentSessionIdRef.current = persistedSessionId
        onSessionStart?.(persistedSessionId)

        const baseElapsed = Number.isFinite(persistedState.elapsedSeconds) && persistedState.elapsedSeconds >= 0
            ? Math.trunc(persistedState.elapsedSeconds)
            : 0
        const persistedRunning = Boolean(persistedState.isRunning)
        const persistedAt = Number(persistedState.persistedAt)
        const elapsedDrift = persistedRunning && Number.isFinite(persistedAt)
            ? Math.max(0, Math.floor((Date.now() - persistedAt) / 1000))
            : 0
        const nextElapsedSeconds = Math.max(0, baseElapsed + elapsedDrift)

        const offsetTimestamp = new Date()
        offsetTimestamp.setSeconds(offsetTimestamp.getSeconds() - nextElapsedSeconds)
        reset(offsetTimestamp, persistedRunning)
        setStart(nextElapsedSeconds === 0 ? Boolean(persistedState.isStart) : false)

        setHasHydratedState(true)
    }, [onSessionStart, reset])

    useEffect(() => {
        if (!hasHydratedState) {
            return
        }

        const payload = {
            version: 1,
            isStart,
            isMuted,
            isRunning,
            elapsedSeconds,
            currentSessionId: currentSessionIdRef.current ?? null,
            persistedAt: Date.now(),
        }

        try {
            localStorage.setItem(STOPWATCH_STATE_STORAGE_KEY, JSON.stringify(payload))
        } catch (error) {
            console.warn('Failed to persist stopwatch state:', error)
        }
    }, [elapsedSeconds, hasHydratedState, isMuted, isRunning, isStart])

    const completeActiveSession = async () => {
        const completedSessionId = currentSessionIdRef.current
        if (!completedSessionId) {
            setCompletionError('Missing session id – cannot complete session')
            return
        }

        setIsCompleting(true)
        setCompletionError('')

        try {
            await completeSession(completedSessionId)
            currentSessionIdRef.current = null
            setCompleted(false)
            setStart(true)
            onSessionComplete?.(completedSessionId)
            reset(new Date(), false)
        } catch (error) {
            setCompletionError(error instanceof Error ? error.message : 'Failed to complete session')
        } finally {
            setIsCompleting(false)
        }
    }

    return (
        <div className='text-center'>
            <div className='text-[100px] text-[var(--text-primary)]'>
                <span>{hours < 10 ? `0${hours}` : hours}</span>:<span>{minutes < 10 ? `0${minutes}` : minutes}</span>:<span>{seconds < 10 ? `0${seconds}` : seconds}</span>
            </div>

            {!isRunning && isStart && (
                <button className="inline-flex items-center gap-2 rounded-md bg-gray-800 hover:bg-gray-700 active:bg-gray-600 px-4 py-2 text-sm font-medium text-gray-200 border border-gray-700/70 shadow-sm shadow-black/40 transition"
                        disabled={isCompleting}
                        onClick={async () => {
                            try {
                                currentSessionIdRef.current = await startSession({
                                    taskId: task || null,
                                    projectId: project || null,
                                })
                                setCompletionError('')
                                onSessionStart?.(currentSessionIdRef.current)
                                start()
                                setStart(false)
                                if (!isMuted) {
                                    sessionAudio.play()
                                }
                            } catch (error) {
                                console.error('Session start failed:', error)
                            }
                        }}>Start Session</button>)}

            {isRunning && (<button className="inline-flex items-center gap-2 rounded-md bg-gray-800 hover:bg-gray-700 active:bg-gray-600 px-4 py-2 text-sm font-medium text-gray-200 border border-gray-700/70 shadow-sm shadow-black/40 transition" onClick={() => {
                pause()
                sessionAudio.pause()
            }}>Pause</button>)}

            {!isRunning && !isCompleted && !isStart && (
                <button className="inline-flex items-center gap-2 rounded-md bg-gray-800 hover:bg-gray-700 active:bg-gray-600 px-4 py-2 text-sm font-medium text-gray-200 border border-gray-700/70 shadow-sm shadow-black/40 transition"
                        disabled={isCompleting}
                        onClick={() => {

                    start()
                    if (!isMuted) {
                        sessionAudio.play()
                    }
                }}>Continue</button>)}

            {isRunning && (
                <button className="inline-flex items-center gap-2 ml-4 rounded-md bg-gray-800 hover:bg-gray-700 active:bg-gray-600 px-4 py-2 text-sm font-medium text-gray-200 border border-gray-700/70 shadow-sm shadow-black/40 transition"
                        disabled={isCompleting}
                        onClick={() => {
                    pause()
                    sessionAudio.pause()
                    void completeActiveSession()
                }}>Stop</button>)}

            {!isRunning && !isStart && isCompleting && (
                <p className="text-sm text-gray-300">Saving completed session...</p>
            )}

            {!isRunning && !isStart && !isCompleting && completionError && (
                <div className="space-y-3">
                    <p className="text-sm text-red-300">{completionError}</p>
                    <button
                        className="inline-flex items-center gap-2 rounded-md bg-red-900/40 hover:bg-red-900/60 active:bg-red-900/70 px-4 py-2 text-sm font-medium text-red-100 border border-red-700/60 shadow-sm shadow-black/40 transition"
                        onClick={() => {
                            void completeActiveSession()
                        }}
                    >
                        Retry Completion
                    </button>
                </div>
            )}

            {isCompleted && !isRunning && (
                <button className="inline-flex items-center gap-2 rounded-md bg-gray-800 hover:bg-gray-700 active:bg-gray-600 px-4 py-2 text-sm font-medium text-gray-200 border border-gray-700/70 shadow-sm shadow-black/40 transition" onClick={() => {
                    // Restarts to 5 minutes timer
                    const time = new Date();
                    time.setSeconds(time.getSeconds() + 300);
                    reset(time)
                }}>Start Breakout Time</button>)}

            <div>
                <button className="inline-flex items-center gap-2 mt-4 rounded-md bg-gray-800 hover:bg-gray-700 active:bg-gray-600 px-4 py-2 text-sm font-medium text-gray-200 border border-gray-700/70 shadow-sm shadow-black/40 transition"
                        disabled={isCompleting}
                        onClick={() => {
                            const willBeMuted = !isMuted
                            setMuted(willBeMuted)
                            if (willBeMuted) {
                                sessionAudio.pause()
                            } else if (isRunning) {
                                sessionAudio.play()
                            }
                        }}>{isMuted ? <VolumeOffIcon/> : <VolumeUpIcon/>}
                </button>
            </div>
        </div>
    );
}

export default MyTimer
